import type { TFunction } from 'i18next';
import type { SubmitEventHandler } from 'react';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { getTestProps } from '../../lib/helpers';

import { useAppForm } from '../../form-hooks/form';

export type ApiKeyAccessFormSubmitFn = ({
  value,
}: {
  value: { space: string; permissions?: string };
}) => void;

interface ApiKeyAccessFormProps {
  /**
   * Callback function to handle form submission.
   */
  onSubmit: ApiKeyAccessFormSubmitFn;
  /**
   * List of spaces to select from.
   */
  spaces?: { id: string; name: string }[];
  /**
   * Optional flag to disable the form.
   */
  disabled?: boolean;
  /**
   * Optional flag to show permissions selector
   */
  showPermissions?: boolean;
  /**
   * Component test ID
   */
  testId?: string;
}

const schemaBuilder = (showPermissions: boolean, t: TFunction) => {
  const baseSchema = z.object({
    space: z.string().min(1, t('Form.FormErrorNotBlank')),
  });

  if (showPermissions) {
    return baseSchema.extend({
      permissions: z.string().min(1, t('Form.FormErrorNotBlank')),
    });
  }

  return baseSchema;
};

const ApiKeyAccessForm = ({
  onSubmit,
  spaces,
  disabled,
  showPermissions = false,
  testId,
}: ApiKeyAccessFormProps) => {
  const { t } = useTranslation();

  const validationSchema = useMemo(
    () => schemaBuilder(showPermissions, t),
    [showPermissions, t],
  );

  const form = useAppForm({
    defaultValues: {
      space: '',
      ...(showPermissions && { permissions: '' }),
    },
    onSubmit: onSubmit,
    validators: {
      onChange: validationSchema,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  const permissionsOptions = useMemo(
    () => [
      { value: 'read', label: t('ApiKeyAccess.Permissions.Read') },
      { value: 'read write', label: t('ApiKeyAccess.Permissions.ReadWrite') },
    ],
    [t],
  );

  const spaceOptions = useMemo(
    () =>
      (spaces || []).map(({ id, name }) => ({
        value: id,
        label: name,
      })),
    [spaces],
  );

  return (
    <form.AppForm>
      <form
        id="access-keys"
        className="w-full"
        onSubmit={handleSubmit}
        noValidate={true}
      >
        <p className="my-4 text-center w-full">
          {t('ApiKeyAccess.SelectSpace')}
        </p>
        <form.AppField name="space">
          {(field) => (
            <field.Dropdown
              options={spaceOptions}
              placeholder={t('Spaces.SpaceSelector.PlaceHolder')}
              emptyOptions={
                <div className="py-2 px-4 dark:text-slate-200">
                  {t('Spaces.SpaceSelector.EmptySpaces')}
                </div>
              }
              additionalClasses="max-w-sm mx-auto"
              additionalDropdownClasses="border"
              disabled={disabled}
              hideSearch
              {...getTestProps(testId, 'spaces', 'testId')}
            />
          )}
        </form.AppField>
        {showPermissions && (
          <div className="my-2 mx-auto w-fit">
            <p className="my-4 text-center w-fit">
              {t('ApiKeyAccess.SelectPermissions')}
            </p>
            <form.AppField name="permissions">
              {(field) => (
                <field.RadioGroup
                  additionalClasses="max-w-sm"
                  disabled={disabled}
                  options={permissionsOptions}
                ></field.RadioGroup>
              )}
            </form.AppField>
          </div>
        )}
      </form>
    </form.AppForm>
  );
};
export default ApiKeyAccessForm;
